import * as React from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
} from "@mui/material";
import { CardBase, StyledTextField } from "../../components/global";
import { ProjectTask } from "../../types/project";
import { Template } from "../../types/template";
import { useNavigate, useParams } from "react-router-dom";
import {
  useAddTemplateMutation,
  useEditTemplateMutation,
  useGetTemplateByIdQuery,
} from "../../services/template.service";
import { useTitle } from "../../hooks/useTitle";
import { Controller, useForm } from "react-hook-form";
import useScroll from "../../hooks/useScroll";
import { useUILabel } from "../../hooks/useUILabel";
import { LoadingButton, LoadingButtonProps } from "@mui/lab";
import { toast } from "react-toastify";
import SelectSimple from "../../components/SelectSimple";
import SelectLocation from "../../components/SelectLocation";
import SelectSkills from "../../components/SelectSkills";
import SelectCertifications from "../../components/SelectCertification";
import StickyBox from "react-sticky-box";
import LocationModalForm from "../location/LocationModalForm";
import { Location } from "../../types/location";
import { Customer } from "../../types/customer";
import ClientModalForm from "../client/ClientModalForm";
import { useGetProjectByIdQuery } from "../../services/project.service";
import { decode } from "html-entities";
import { styled } from "@mui/material/styles";

const StyledNavigationList = styled(List)(() => ({
  padding: 0,
  margin: 0,
}));

const StyledNavigationButton = styled(ListItemButton)(({ theme }) => ({
  borderRadius: 10,
  margin: "6px 0",
  padding: "12px 18px",
  minHeight: 48,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: "3px",
    background: theme.palette.primary.main,
    transform: "scaleY(0)",
    transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    borderRadius: "0 3px 3px 0",
  },
  "&:hover": {
    backgroundColor: theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.1)"
      : "rgba(0, 0, 0, 0.05)",
    transform: "translateX(6px)",
    boxShadow: theme.palette.mode === "dark"
      ? "0 4px 12px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)"
      : "0 4px 12px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.8)",
    "&::before": {
      transform: "scaleY(1)",
    },
    "& .MuiListItemText-primary": {
      color: theme.palette.primary.main,
      fontWeight: 600,
    },
  },
  "& .MuiListItemText-primary": {
    fontSize: "0.95rem",
    fontWeight: 500,
    color: theme.palette.text.primary,
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  },
}));

const pricingOptions = [
  { value: "hourly", label: "Hourly Rate" },
  { value: "fixed", label: "Fixed Rate" },
];

const ProjectTemplateEditPage = () => {
  const { templateId, projectId } = useParams();
  const navigate = useNavigate();
  const selectLocationLabel = useUILabel("project.selectLocation");
  const addNewLocationLabel = useUILabel("project.addNewLocation");
  const numberOfIPsRequiredShortLabel = useUILabel("project.numberOfIPsRequiredShort");
  const paymentTypeLabel = useUILabel("project.paymentType");
  const confidentialInformationLabel = useUILabel("project.confidentialInformation");
  const editTemplateLabel = "Edit Template"; // TODO: Add to labels if needed
  const createTemplateFromWorkLabel = "Create Template From Work"; // TODO: Add to labels if needed
  const createNewTemplateLabel = "Create New Template"; // TODO: Add to labels if needed
  const workSiteLabel = useUILabel("project.workSite");
  const addressLabel = useUILabel("common.address");
  const additionalInformationLabel = useUILabel("project.additionalInformation");
  const updateLabel = useUILabel("common.update");
  const createLabel = useUILabel("common.create");

  const [scrollToBasic, basicRef] = useScroll();
  const [scrollToLocation, locationRef] = useScroll();
  const [scrollToSkill, skillRef] = useScroll();
  const [scrollToMore, moreRef] = useScroll();
  const [scrollToCertification, certificationRef] = useScroll();
  //const [newTasks, setNewTasks] = React.useState<Array<ProjectTask>>([]);

  const [locationModal, setLocationModal] = React.useState<{
    open: boolean;
    data: Location | null;
  }>({
    open: false,
    data: null,
  });
  const [clientModal, setClientModal] = React.useState<{
    open: boolean;
    data: Customer | null;
  }>({
    open: false,
    data: null,
  });

  const { data: dataProject, isLoading: loadingProject } =
    useGetProjectByIdQuery(parseInt(projectId!), {
      skip: !projectId,
    });
  const { data: dataTemplate, isLoading: loadingTemplate } =
    useGetTemplateByIdQuery(parseInt(templateId!), {
      skip: !templateId,
    });
  const [add, { isLoading: adding }] = useAddTemplateMutation();
  const [edit, { isLoading: updating }] = useEditTemplateMutation();
  const [target, setTarget] = React.useState("location");

  useTitle(
    !!dataTemplate
      ? editTemplateLabel
      : !!dataProject
        ? createTemplateFromWorkLabel
        : createNewTemplateLabel
  );

  const data = React.useMemo(() => {
    if (!!dataTemplate) return dataTemplate;
    else if (!!dataProject) return dataProject;
    else return null;
  }, [dataTemplate, dataProject]);

  const {
    handleSubmit,
    control,
    setValue,
    setError,
    formState: { errors },
  } = useForm();

  const onSubmit = async (payload: any) => {
    try {
      if (target === "customer" && !payload.customer) {
        setError("customer", { type: "custom", message: "Invalid" });
        return;
      } else if (target === "location" && !payload.location) {
        setError("location", { type: "custom", message: "Invalid" });
        return;
      }
      if (target === "customer") payload.location = undefined;
      else payload.customer = undefined;

      // if (newTasks.length > 0) {
      //   payload.tasks = [...newTasks];
      // }

      if (!data || dataProject) {
        const template: Template = await add(payload).unwrap();
        toast.success("Template created");
        navigate(`/works/templates/${template.id}`, { replace: true });
      } else {
        if (!payload.password) delete payload.password;
        payload.id = data.id;
        await edit(payload).unwrap();
        toast.success("Template updated");
        navigate(`/works/templates/${data.id}`, { replace: true });
      }
    } catch (err: any) {
      toast.error(err);
    }
  };

  const StickyMenu = React.useCallback(
    () => (
      <StickyBox offsetTop={70} offsetBottom={20}>
        <CardBase
          title="Go to:"
          useGlassCard
          glassEffect="clear"
          sx={(theme) => ({
            p: 0,
            "& .MuiCardHeader-root": {
              px: 3,
              pb: 1,
            },
            "& .MuiTypography-h6": {
              fontWeight: 700,
              fontSize: "1.05rem",
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              letterSpacing: "0.5px",
            },
            "& .MuiCardContent-root": {
              pt: 0.5,
              pb: 2.5,
              px: 2.5,
            },
          })}
        >
          <StyledNavigationList disablePadding>
            <ListItem disablePadding>
              <StyledNavigationButton onClick={scrollToBasic as any}>
                <ListItemText primary="General" />
              </StyledNavigationButton>
            </ListItem>
            <ListItem disablePadding>
              <StyledNavigationButton onClick={scrollToLocation as any}>
                <ListItemText primary="Select Work Site" />
              </StyledNavigationButton>
            </ListItem>
            <ListItem disablePadding>
              <StyledNavigationButton onClick={scrollToSkill as any}>
                <ListItemText primary="Select/Search Skills" />
              </StyledNavigationButton>
            </ListItem>
            <ListItem disablePadding>
              <StyledNavigationButton onClick={scrollToCertification as any}>
                <ListItemText primary="Add Certifications (if needed)" />
              </StyledNavigationButton>
            </ListItem>
            {/* <ListItem disablePadding>
              <StyledNavigationButton onClick={scrollToTask as any}>
                <ListItemText primary="Add Tasks (if needed)" />
              </StyledNavigationButton>
          </ListItem> */}
            <ListItem disablePadding>
              <StyledNavigationButton onClick={scrollToMore as any}>
                <ListItemText primary="Final Step: Additional Information" />
              </StyledNavigationButton>
            </ListItem>
          </StyledNavigationList>
        </CardBase>
      </StickyBox>
    ),
    [scrollToBasic, scrollToLocation, scrollToSkill, scrollToCertification, scrollToMore]
  );

  // const handleAddTask = (task: ProjectTask) => {
  //   setTaskModal({ open: false, data: null });
  //   setNewTasks([...newTasks, task]);
  // };
  // const handleDeleteTask = (task: ProjectTask) => {
  //   const newList = newTasks.filter((it) => it.id != task.id);
  //   setNewTasks(newList);
  // };

  React.useEffect(() => {
    setValue("title", decode(data?.title) || "");
    if (!!data && "tags" in data) setValue("tags", data?.tags || "");
    else setValue("tags", "work,template");
    setValue("description", decode(data?.description) || "");
    setValue(
      "confidential_information",
      decode(data?.confidential_information) || ""
    );
    setValue(
      "approximate_hours_to_complete",
      data?.approximate_hours_to_complete || ""
    );
    setValue("location", data?.location_id);
    setValue("customer", data?.customer_id);

    if (!!data?.customer_id) {
      setTarget("customer");
    } else {
      // default on init
      setTarget("location");
    }

    const skillIds = data?.skills?.map((it) => it.id);
    setValue("skills", skillIds || []);

    const certIds = data?.certifications?.map((it) => it.id);
    setValue("certifications", certIds || []);

    //setNewTasks(data?.tasks || []);

    setValue("is_photo_required", !!data?.is_photo_required || false);
    setValue(
      "is_final_notes_required",
      !!data?.is_final_notes_required || false
    );
    setValue("tool_required", !!data?.tool_required || false);
    //setValue("covid_vaccine_required", !!data?.covid_vaccine_required || false);
    setValue(
      "background_check_required",
      !!data?.background_check_required || false
    );
    setValue("drug_test_required", !!data?.drug_test_required || false);
    setValue("mile_radius", data?.mile_radius);
    setValue("type", data?.type || "hourly");
    setValue("num_of_talent", data?.num_of_talent || 1);
  }, [data, setValue]);

  const submitLabel = !!data && !dataProject ? updateLabel : createLabel;
  const renderSubmitButton = (overrides?: LoadingButtonProps) => (
    <LoadingButton
      type="submit"
      variant="contained"
      size="large"
      disabled={loadingTemplate || loadingProject}
      loading={adding || updating}
      sx={{
        width: { xs: "100%", sm: "auto" },
        minWidth: { xs: "100%", sm: "auto" },
        maxWidth: { xs: "100%", sm: "200px" },
        ...(overrides?.sx || {}),
      }}
      {...overrides}
    >
      {submitLabel}
    </LoadingButton>
  );

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 1 }}>
        <Grid item xs={12} lg={3} sx={{ display: { xs: "none", lg: "block" } }}>
          <StickyMenu />
        </Grid>
        <Grid item xs={12} lg={9}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ pb: 4 }}>
              {(loadingProject || loadingTemplate) && <CircularProgress />}
              {/* <Card sx={{ mt: 1 }} ref={basicRef}>
                <CardHeader
                  title="Template Info"
                  titleTypographyProps={{ fontSize: 18, color: "#17903d" }}
                />
                <CardContent>
                  <Controller
                    name="tags"
                    control={control}
                    defaultValue=""
                    rules={{ required: true }}
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                      <TextField
                        InputLabelProps={{ shrink: !!value }}
                        label="Keywords"
                        value={value}
                        fullWidth
                        onChange={onChange}
                        onBlur={onBlur}
                        error={!!errors.tags}
                        helperText={!!errors.tags && "This field is required"}
                      />
                    )}
                  />
                  <FormHelperText>
                    Separate template keywords with a comma
                  </FormHelperText>
                </CardContent>
              </Card> */}
              <CardBase
                title="General"
                useGlassCard={true}
                nested={false}
                sx={{ mt: 1 }}
                ref={basicRef}
              >
                <Controller
                  name="title"
                  control={control}
                  defaultValue=""
                  rules={{ required: true }}
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <StyledTextField
                      label="Work ID# - Work Title"
                      value={value || ""}
                      fullWidth
                      onChange={onChange}
                      onBlur={onBlur}
                      error={!!errors.title}
                      helperText={!!errors.title && "This field is required"}
                      sx={{ mb: 2 }}
                    />
                  )}
                />
                <Controller
                  name="description"
                  control={control}
                  defaultValue=""
                  rules={{ required: true }}
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <StyledTextField
                      label="Please enter Description/Scope of Work"
                      multiline={true}
                      minRows={10}
                      value={value || ""}
                      fullWidth
                      onChange={onChange}
                      onBlur={onBlur}
                      error={!!errors.description}
                      helperText={
                        !!errors.description && "This field is required"
                      }
                      sx={{ mb: 2 }}
                    />
                  )}
                />
                <Controller
                  name="confidential_information"
                  control={control}
                  defaultValue=""
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <StyledTextField
                      label={confidentialInformationLabel}
                      multiline={true}
                      minRows={2}
                      value={value || ""}
                      fullWidth
                      onChange={onChange}
                      onBlur={onBlur}
                      sx={{ mb: 2 }}
                    />
                  )}
                />
                <Controller
                  name="num_of_talent"
                  control={control}
                  defaultValue=""
                  rules={{ required: true }}
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <StyledTextField
                      label={numberOfIPsRequiredShortLabel}
                      value={value || ""}
                      fullWidth
                      type={"number"}
                      onChange={onChange}
                      onBlur={onBlur}
                      error={!!errors.num_of_talent}
                      helperText={
                        !!errors.num_of_talent && "This field is required"
                      }
                      sx={{ mb: 2 }}
                    />
                  )}
                />
                <Controller
                  name="type"
                  control={control}
                  defaultValue="hourly"
                  render={({ field: { onChange, value } }) => (
                    <SelectSimple
                      title={paymentTypeLabel}
                      data={pricingOptions}
                      value={value}
                      onChange={(e) => onChange(e.target.value as string)}
                      sx={{ mt: 2 }}
                    />
                  )}
                />
                <Controller
                  name="approximate_hours_to_complete"
                  control={control}
                  defaultValue=""
                  rules={{ required: true, min: 1 }}
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <StyledTextField
                      label="Estimated Amount of Hours"
                      value={value || ""}
                      type="number"
                      fullWidth
                      onChange={onChange}
                      sx={{ mt: 2, mb: 2 }}
                      onBlur={onBlur}
                      error={!!errors.approximate_hours_to_complete}
                      helperText={
                        !!errors.approximate_hours_to_complete &&
                        "This field is required"
                      }
                    />
                  )}
                />
                <Controller
                  name="mile_radius"
                  control={control}
                  defaultValue=""
                  rules={{ required: true, min: 1 }}
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <StyledTextField
                      label="Worksite Radius (miles)"
                      value={value || ""}
                      type="number"
                      fullWidth
                      onChange={onChange}
                      onBlur={onBlur}
                      error={!!errors.mile_radius}
                      helperText={
                        !!errors.mile_radius
                          ? "This field is required"
                          : "Only available to IPs within radius"
                      }
                    />
                  )}
                />
              </CardBase>
              <CardBase
                title={workSiteLabel}
                useGlassCard={true}
                nested={false}
                sx={{ mt: 1 }}
                ref={locationRef}
              >
                {/* <Controller
                    name="target"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <SelectSimple
                        title="Location Type"
                        data={targetOptions}
                        value={target}
                        onChange={(e) => setTarget(e.target.value as string)}
                        sx={{ mb: 2 }}
                      />
                    )}
                  />
                  {target == "customer" && (
                    <>
                      <Controller
                        name="customer"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <SelectClient
                            label="Select a saved Client"
                            onChange={onChange}
                            value={value}
                            sx={{ mt: 0 }}
                            error={!!errors.customer}
                            helperText={
                              !!errors.customer && "This field is required"
                            }
                          />
                        )}
                      />
                      <Button
                        variant="text"
                        onClick={() =>
                          setClientModal({ open: true, data: null })
                        }
                      >
                        + Add New Client
                      </Button>
                    </>
                  )} */}
                {target == "location" && (
                  <>
                    <Controller
                      name="location"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <SelectLocation
                          label={selectLocationLabel}
                          onChange={onChange}
                          value={value}
                          sx={{ mt: 0 }}
                          error={!!errors.location}
                          helperText={
                            !!errors.location && "This field is required"
                          }
                        />
                      )}
                    />
                    <Button
                      variant="text"
                      onClick={() =>
                        setLocationModal({ open: true, data: null })
                      }
                    >
                      {addNewLocationLabel}
                    </Button>
                  </>
                )}
                {target === "new" && (
                  <Controller
                    name="address"
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                      <StyledTextField
                        label={addressLabel}
                        value={value || ""}
                        fullWidth
                        onChange={onChange}
                        onBlur={onBlur}
                        sx={{ mt: 0 }}
                        error={!!errors.address}
                        helperText={
                          !!errors.address && "This field is required"
                        }
                      />
                    )}
                  />
                )}
              </CardBase>
              <CardBase
                title="Required Skills"
                useGlassCard={true}
                nested={false}
                sx={{ mt: 1 }}
                ref={skillRef}
              >
                <Controller
                  name="skills"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { onChange, value } }) => (
                    <SelectSkills
                      label="Select/Search Skills"
                      onChange={onChange}
                      values={value}
                      error={!!errors.skills}
                      helperText={!!errors.skills && "This field is required"}
                    />
                  )}
                />
              </CardBase>
              <CardBase
                title="Required Certifications (optional)"
                useGlassCard={true}
                nested={false}
                sx={{ mt: 1 }}
                ref={certificationRef}
              >
                <Box>
                  <Controller
                    name="certifications"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <SelectCertifications
                        label="Select Certifications"
                        onChange={onChange}
                        values={value}
                      />
                    )}
                  />
                </Box>
              </CardBase>
              {/* <Card sx={{ mt: 1 }} ref={taskRef}>
                <CardHeader
                  title="Tasks"
                  titleTypographyProps={{ fontSize: 18, color: "#17903d" }}
                  action={
                    <Button
                      // variant="outlined"
                      onClick={() => setTaskModal({ open: true, data: null })}
                      startIcon={<Add />}
                    >
                      Add Task
                    </Button>
                  }
                />
                <CardContent>
                  <List>
                    {newTasks.map((it, idx) => (
                      <ListItem
                        sx={{ backgroundColor: "#f5f5f5", mt: 1, p: 2 }}
                        secondaryAction={
                          <IconButton
                            edge="end"
                            onClick={() => handleDeleteTask(it)}
                          >
                            <Delete />
                          </IconButton>
                        }
                      >
                        <ListItemText
                          key={idx}
                          primary={it.name}
                          secondary={it.description}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card> */}
              <CardBase
                title={additionalInformationLabel}
                useGlassCard={true}
                nested={false}
                sx={{ mt: 1 }}
                ref={moreRef}
              >
                <Controller
                  name="is_photo_required"
                  control={control}
                  defaultValue={false}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox {...field} checked={field.value} />}
                      label="Photo(s) Required - IPs must submit photo(s) of their work. Specify details in Scope Of Work"
                    />
                  )}
                />
                <br />
                <Controller
                  name="is_final_notes_required"
                  control={control}
                  defaultValue={false}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox {...field} checked={field.value} />}
                      label="Final Notes Required - IPs must submit final notes of their work. Specify details in Scope Of Work"
                    />
                  )}
                />
                <br />
                <Controller
                  name="tool_required"
                  control={control}
                  defaultValue={false}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox {...field} checked={field.value} />}
                      label="Tool(s) Required - IPs need to provide their own tool(s). Specify details in Scope Of Work"
                    />
                  )}
                />
                <br />
                <Controller
                  name="background_check_required"
                  control={control}
                  defaultValue={false}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox {...field} checked={field.value} />}
                      label="Background check required"
                    />
                  )}
                />
                <br />
                <Controller
                  name="drug_test_required"
                  control={control}
                  defaultValue={false}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox {...field} checked={field.value} />}
                      label="Drug test required"
                    />
                  )}
                />
              </CardBase>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent={{ xs: "stretch", sm: "flex-end" }}
                sx={{ mt: 2 }}
              >
                {renderSubmitButton()}
              </Stack>
            </Box>
          </form>
        </Grid>
      </Grid>
      {/* <ModalTaskForm
        open={taskModal.open}
        onClose={() => setTaskModal({ open: false, data: null })}
        data={taskModal.data}
        onAdd={handleAddTask}
      /> */}
      <LocationModalForm
        open={locationModal.open}
        onClose={() => setLocationModal({ open: false, data: null })}
        onCreated={(newId) => setValue("location", newId)}
        data={locationModal.data}
      />
      <ClientModalForm
        open={clientModal.open}
        onClose={() => setClientModal({ open: false, data: null })}
        onCreated={(newId) => setValue("customer", newId)}
        data={clientModal.data}
      />
    </Box>
  );
};
export default ProjectTemplateEditPage;
